import { useApi } from '@gravitate-js/the-armory'
import { type UseMutationOptions, type UseQueryResult, useMutation, useQuery } from '@tanstack/react-query'
import { getAPIURL } from '@utils/api'
import {
  type AddCommentRequest as AddCommentReq,
  type AddNoteRequest as AddNoteReq,
  type ApproveRebillRequest as ApproveRebillReq,
  CreditRebillResponse,
  type ImpactedTransactionRequest,
  type Invoice,
  InvoiceComment,
  type InvoiceDetail,
  type InvoiceGroup,
  type InvoiceQueryParams,
  type InvoiceResponse,
  type InvoiceTransaction,
  type ManageHoldRequest as ManageHoldReq,
  type RebillSaveBody,
  type RejectRebillRequest as RejectRebillReq,
  type RemoveCommentRequest as RemoveCommentReq,
  type RemoveNoteRequest as RemoveNoteReq,
  TransactionQueryKeys,
} from './types'

export const useInvoices = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })

  const useInvoicesQuery = (params?: InvoiceQueryParams): UseQueryResult<InvoiceResponse> => {
    return useQuery({
      queryKey: ['invoices-be-query', params],
      queryFn: async () => {
        const hasInvoiceOrOrderFilter = params?.filter?.invoice_number || params?.filter?.order

        if (hasInvoiceOrOrderFilter) {
          const currentDate = new Date()
          const twoYearsBack = new Date(currentDate)
          twoYearsBack.setFullYear(currentDate.getFullYear() - 2)
          const oneYearForward = new Date(currentDate)
          oneYearForward.setFullYear(currentDate.getFullYear() + 1)

          const response = await api.post('freight/invoice/overview', {
            start_date: twoYearsBack.toISOString(),
            end_date: oneYearForward.toISOString(),
            filter: params?.filter,
            book_type: params?.book_type,
          })

          return {
            row_count: response?.rows?.length || 0,
            rows: response?.rows || [],
            params: response?.params,
          }
        }

        const openQuery = api.post('freight/invoice/overview', {
          state: 'open',
          filter: params?.filter,
          book_type: params?.book_type,
        })

        const sentQuery = api.post('freight/invoice/overview', {
          start_date: params?.start_date.toISOString(),
          end_date: params?.end_date.toISOString(),
          state: 'sent',
          filter: params?.filter,
          book_type: params?.book_type,
        })

        const [openRes, sentRes] = await Promise.all([openQuery, sentQuery])

        const combinedRows = [...(openRes?.rows || []), ...(sentRes?.rows || [])]

        return {
          row_count: combinedRows.length,
          rows: combinedRows,
          params: openRes?.params,
        }
      },
      refetchOnWindowFocus: false,
    })
  }

  const useSaveRebillInvoiceMutation = () =>
    useMutation<void, Error, RebillSaveBody>({
      mutationFn: async (rebill: RebillSaveBody) => {
        await api.post('freight/invoice/save_rebill', rebill)
      },
    })
  const useInvoiceById = (id?: string) =>
    useQuery<InvoiceGroup>({
      queryKey: [TransactionQueryKeys.invoice, id],
      queryFn: async () => {
        const response = await api.post('freight/invoice/overview', {
          invoice_group_id: id,
          start_date: '2023-01-01',
          end_date: '2030-01-31',
        })

        if (!response?.rows?.length) {
          return null
        }

        // if the response has rows, then grab the first row and loop through the transactions - for each transaction, put the book_type on all of the details
        const firstRow = response?.rows?.[0]

        if (firstRow) {
          for (const invoice of firstRow.invoices) {
            for (const transaction of invoice.transactions) {
              for (const detail of transaction.details) {
                detail.book_type = firstRow.book_type
                detail.customer_id = transaction.customer_id
              }
            }
          }
        }
        return firstRow
      },
      enabled: !!id, // Only run query if id is provided
    })

  const useSendInvoiceMutation = () =>
    useMutation<InvoiceGroup, Error, { invoice_group_id: string }>({
      mutationFn: async ({ invoice_group_id }) => {
        const response = await api.post('freight/invoice/send', undefined, {
          queryParams: new URLSearchParams({ invoice_group_id }),
        })
        return response.data
      },
    })

  const useResendInvoiceMutation = () =>
    useMutation<InvoiceGroup, Error, { invoice_group_id: string; invoice_number: string }>({
      mutationFn: async ({ invoice_group_id, invoice_number }) => {
        const response = await api.post('freight/invoice/re-send', undefined, {
          queryParams: new URLSearchParams({
            invoice_group_id,
            invoice_number,
          }),
        })
        return response
      },
    })

  const useAddCommentMutation = () =>
    useMutation<void, Error, AddCommentReq>({
      mutationFn: async (data: AddCommentReq) => {
        await api.post('freight/invoice/comment', data)
      },
    })

  const useRemoveCommentMutation = () =>
    useMutation<void, Error, RemoveCommentReq>({
      mutationFn: async (data: RemoveCommentReq) => {
        await api.post('freight/invoice/remove_comment', data)
      },
    })

  const useRejectRebillMutation = () =>
    useMutation<void, Error, RejectRebillReq>({
      mutationFn: async (data: RejectRebillReq) => {
        await api.post('freight/invoice/reject_rebill', data)
      },
    })
  const useApproveRebillMutation = () =>
    useMutation<void, Error, ApproveRebillReq>({
      mutationFn: async (data: ApproveRebillReq) => {
        await api.post('freight/invoice/approve_rebill', data)
      },
    })

  const useImpactedTransactionMutation = () =>
    useMutation<InvoiceDetail[], Error, ImpactedTransactionRequest>({
      mutationFn: async (data: ImpactedTransactionRequest) => {
        const response = await api.post<InvoiceDetail[]>('freightinvoice/rebill/impacted_transactions', data)

        return response
      },
    })

  const useAddNoteMutation = () =>
    useMutation<void, Error, AddNoteReq>({
      mutationFn: async (data: AddNoteReq) => {
        await api.post('freight/invoice/add_note', data)
      },
    })

  const useRemoveNoteMutation = () =>
    useMutation<void, Error, RemoveNoteReq>({
      mutationFn: async ({ invoice_group_id, invoice_number }) => {
        await api.post('freight/invoice/remove_note', undefined, {
          queryParams: new URLSearchParams({
            invoice_group_id,
            invoice_number,
          }),
        })
      },
    })

  const useManageHoldMutation = (options?: Omit<UseMutationOptions<void, Error, ManageHoldReq>, 'mutationFn'>) =>
    useMutation<void, Error, ManageHoldReq>({
      mutationFn: async (data: ManageHoldReq) => {
        await api.post('freight/invoice/manage_hold', data)
      },
      ...options,
    })

  return {
    useInvoicesQuery,
    useInvoiceById,
    useRejectRebillMutation,
    useSaveRebillInvoiceMutation,
    useSendInvoiceMutation,
    useResendInvoiceMutation,
    useAddCommentMutation,
    useRemoveCommentMutation,
    useApproveRebillMutation,
    useImpactedTransactionMutation,
    useAddNoteMutation,
    useRemoveNoteMutation,
    useManageHoldMutation,
  }
}
